import { t } from '../core/i18n.js';
import { device } from '../core/device.js';
import { tvnav } from '../core/tvnav.js';
import { notify } from '../core/notify.js';
import { escapeHtml } from '../core/utils.js';
import { report } from '../core/report.js';
import { presetById } from '../core/presets.js';

/* Pantalla «Denunciar contenido». Con `target` ({ type:'archive', id, title }) la denuncia es sobre esa película de «Cine
   clásico»; sin él, es una denuncia general (se indica en texto sobre qué contenido es). Cualquiera puede enviarla. */

const KINDS = ['copyright', 'illegal', 'inappropriate', 'broken', 'other'];
/** De dónde sale el contenido denunciado: la dirección de la lista pública o la ficha en Internet Archive. */
function sourceOf(target) {
    if (!target) return '';
    if (target.type === 'archive') return /^ia-[A-Za-z0-9._-]+$/.test(String(target.id)) ? 'https://archive.org/details/' + String(target.id).slice(3) : '';
    const preset = presetById(target.id);
    return preset && preset.m3uUrl ? preset.m3uUrl : '';
}
const domainOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch (e) { return ''; } };

const ERR = ['bad_email', 'description_short', 'copyright_fields', 'target_required', 'rate_limited', 'network', 'unavailable', 'unknown_target'];

export function openReport({ target = null, prefill = '' } = {}) {
    const app = window.app;
    const id = 'rep-' + Date.now().toString(36);
    const popup = app.popup.create({
        content: `<div class="popup catpick-popup admin-popup support-popup" id="${id}">
            <div class="page">
                <div class="navbar"><div class="navbar-bg"></div><div class="navbar-inner">
                    <div class="title">${t('report.title')}</div>
                    <div class="right"><a href="#" class="link popup-close btn-icon" aria-label="${t('common.close')}"><span class="mi">close</span></a></div>
                </div></div>
                <div class="page-content"><div class="phone-body" data-rep-body></div></div>
            </div>
        </div>`,
        on: {
            open(p) {
                const el = p.el || document.getElementById(id);
                const body = el.querySelector('[data-rep-body]');
                const show = (html) => { body.innerHTML = html; if (device.isTV || !device.hasTouch) setTimeout(() => tvnav.focusLayer(el), 60); };
                const form = () => {
                    show(`<p class="pw-note">${t('report.intro')}</p>
                        ${target ? `<div class="phone-status"><b>${escapeHtml(target.title)}</b><br>${t(target.type === 'channel' ? 'report.targetChannel' : 'report.targetArchive')}${sourceOf(target) ? `<br><small class="muted">${t('report.source')}: <b>${escapeHtml(domainOf(sourceOf(target)))}</b> · ${escapeHtml(sourceOf(target))}</small>` : ''}</div>`
                            : `<div class="field"><label>${t('report.what')}</label><input name="targetText" maxlength="500" autocomplete="off" value="${escapeHtml(prefill)}" data-nav-default/></div>`}
                        <div class="field"><label>${t('report.kind')}</label><select name="kind">${KINDS.map((k) => `<option value="${k}">${t('report.kind.' + k)}</option>`).join('')}</select></div>
                        <div class="field"><label>${t('report.description')}</label><textarea name="description" rows="5" maxlength="4000" placeholder="${escapeHtml(t('report.descriptionPh'))}"></textarea></div>
                        <div class="field"><label>${t('report.email')}</label><input name="email" type="email" autocomplete="email" maxlength="320"/></div>
                        <div class="field"><label>${t('report.name')}</label><input name="name" maxlength="120" autocomplete="name"/></div>
                        <div data-copyright hidden>
                            <div class="field"><label>${t('report.role')}</label><select name="role"><option value="owner">${t('report.role.owner')}</option><option value="representative">${t('report.role.representative')}</option><option value="other">${t('report.role.other')}</option></select></div>
                            <div class="field"><label>${t('report.rightsOwner')}</label><input name="rightsOwner" maxlength="200"/></div>
                            <label class="support-diag"><input type="checkbox" name="goodFaith"/> ${t('report.goodFaith')}</label>
                            <div class="field"><label>${t('report.signature')}</label><input name="signature" maxlength="200"/></div>
                        </div>
                        <div class="field-error" data-err hidden></div>
                        <button type="button" class="btn btn-primary btn-lg btn-block" data-send><span class="mi">flag</span>${t('report.send')}</button>`);
                    const q = (n) => body.querySelector('[name="' + n + '"]');
                    const copy = body.querySelector('[data-copyright]');
                    q('kind').addEventListener('change', () => { copy.hidden = q('kind').value !== 'copyright'; });
                    const send = body.querySelector('[data-send]');
                    send.addEventListener('click', async () => {
                        const err = body.querySelector('[data-err]');
                        const kind = q('kind').value;
                        const data = {
                            kind, email: q('email').value.trim(), name: q('name').value.trim(), description: q('description').value.trim(),
                            targetType: target ? target.type : 'general', targetId: target ? target.id : '', targetText: target ? (target.text || '') : q('targetText').value.trim()
                        };
                        if (kind === 'copyright') Object.assign(data, { role: q('role').value, rightsOwner: q('rightsOwner').value.trim(), goodFaith: q('goodFaith').checked, signature: q('signature').value.trim() });
                        err.hidden = true;
                        send.disabled = true;
                        let r = await report.send(data);
                        // Un canal de una lista que Filoteca no conoce se denuncia igualmente, como denuncia general.
                        if (!r.ok && r.error === 'unknown_target' && target && target.type === 'channel') {
                            r = await report.send(Object.assign({}, data, { targetType: 'general', targetId: '', targetText: target.title }));
                        }
                        if (!r.ok) {
                            send.disabled = false;
                            err.textContent = t('report.err.' + (ERR.indexOf(r.error) !== -1 ? r.error : 'unavailable'));
                            err.hidden = false;
                            return;
                        }
                        notify.toast(t('report.sent', { ref: r.ref }), { icon: 'check_circle', duration: 5000 });
                        show(`<div class="phone-status"><b>${escapeHtml(t('report.sent', { ref: r.ref }))}</b></div><p class="pw-note">${t('report.sentNote')}</p>
                            <button type="button" class="btn btn-block popup-close" data-nav-default><span class="mi">check</span>${t('common.close')}</button>`);
                    });
                };
                form();
            },
            closed() {
                try { popup.destroy(); } catch (e) { /* ya destruido */ }
                const el = document.getElementById(id);
                if (el && el.parentNode) el.parentNode.removeChild(el);
            }
        }
    });
    popup.open();
    return popup;
}
